<script setup lang="ts">
import Dialog from 'primevue/dialog'
import { computed, nextTick, ref, useId, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useI18n, type MessageKey } from '@/app/i18n'
import { navTarget, type NavItem } from '@/app/nav/collect'
import { useCurrentSoftware } from '@/shared/composables/useCurrentSoftware'
import AppIcon from '@/shared/ui/AppIcon.vue'

import { flattenNav, searchNav, type NavEntry } from './navbar-items'
import { useShellMenu } from './useShellMenu'

/**
 * The navbar's search (⌘K): a palette over the page listing the pages of the current software's menu, narrowed by
 * what is typed; arrows move the highlight, Enter or a click opens the page. The API has no global record search yet,
 * so the menu is what it searches.
 */
const visible = defineModel<boolean>('visible', { default: false })
const { t } = useI18n()
const router = useRouter()
const { software } = useCurrentSoftware()
const menu = useShellMenu()
const query = ref('')
const highlighted = ref(0)
const input = ref<HTMLInputElement | null>(null)
const listId = `palette-${useId()}`

// Titles are message keys declared with keys.t() in the nav files.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const label = (item: NavItem): string => t(item.title as MessageKey)
const entries = computed(() => flattenNav(menu.value).filter((entry) => entry.item.to))
const results = computed(() => searchNav(entries.value, label, query.value))

watch(visible, (open) => {
  if (!open) return
  query.value = ''
  highlighted.value = 0
})

/** The dialog mounts its content after its opening transition: the field is focused on `show`, not on `visible`. */
function focusInput(): void {
  void nextTick(() => input.value?.focus())
}
watch(results, () => {
  highlighted.value = 0
})

function open(entry: NavEntry): void {
  const target = software.value ? navTarget(entry.item, software.value) : undefined
  visible.value = false
  if (target) void router.push(target)
}

function onKeydown(event: KeyboardEvent): void {
  const last = results.value.length - 1
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    highlighted.value = highlighted.value >= last ? 0 : highlighted.value + 1
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlighted.value = highlighted.value <= 0 ? last : highlighted.value - 1
  } else if (event.key === 'Enter') {
    const entry = results.value[highlighted.value]
    if (!entry) return
    event.preventDefault()
    open(entry)
  }
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    position="top"
    :show-header="false"
    dismissable-mask
    :draggable="false"
    :style="{ width: '560px', maxWidth: 'calc(100vw - 2rem)' }"
    :pt="{ root: { class: 'command-palette', 'data-testid': 'command-palette' } }"
    @show="focusInput"
  >
    <label class="command-palette__field">
      <AppIcon name="SearchIcon" :size="18" class="command-palette__icon" />
      <input
        ref="input"
        v-model="query"
        type="search"
        class="command-palette__input"
        :placeholder="t('shell.navbar.hint.palette')"
        :aria-label="t('shell.navbar.action.search')"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="results.length > 0"
        :aria-controls="results.length ? listId : undefined"
        :aria-activedescendant="results.length ? `${listId}-${highlighted}` : undefined"
        autocomplete="off"
        data-testid="command-palette-input"
        @keydown="onKeydown"
      />
    </label>
    <ul v-if="results.length" :id="listId" role="listbox" class="command-palette__list">
      <li
        v-for="(entry, index) in results"
        :id="`${listId}-${index}`"
        :key="entry.item.id"
        role="option"
        :aria-selected="index === highlighted"
        class="command-palette__option"
        :class="{ 'is-highlighted': index === highlighted }"
        :data-nav="entry.item.id"
        @mousemove="highlighted = index"
        @click="open(entry)"
      >
        <AppIcon :name="entry.item.icon || entry.parent?.icon || 'CircleIcon'" :size="18" class="command-palette__option-icon" />
        <span class="command-palette__option-text">
          <span v-if="entry.parent" class="command-palette__option-parent">{{ label(entry.parent) }}</span>
          <span class="command-palette__option-title">{{ label(entry.item) }}</span>
        </span>
        <AppIcon v-if="index === highlighted" name="CornerDownLeftIcon" :size="14" class="command-palette__enter" />
      </li>
    </ul>
    <p v-else class="command-palette__empty">{{ t('shell.navbar.state.noResults') }}</p>
  </Dialog>
</template>

<style>
/* The dialog is teleported to the body: styled globally. */
.command-palette.p-dialog {
  margin-top: 10vh;
  overflow: hidden;
  border-radius: var(--p-border-radius-xl);
  box-shadow: 0 24px 60px rgb(34 41 47 / 18%);
}
.command-palette .p-dialog-content {
  padding: 0;
}
.command-palette__field {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.9rem 1.1rem;
  color: var(--p-surface-500);
  border-bottom: 1px solid var(--p-content-border-color);
}
.command-palette__input {
  flex: 1 1 auto;
  min-width: 0;
  font: inherit;
  font-size: 1.07rem;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  outline: none;
}
.command-palette__input::-webkit-search-cancel-button {
  display: none;
}
.command-palette__list {
  max-height: 50vh;
  margin: 0;
  padding: 0.5rem;
  overflow-y: auto;
  list-style: none;
}
.command-palette__option {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.6rem 0.75rem;
  color: var(--p-text-color);
  cursor: pointer;
  border-radius: var(--p-border-radius-lg);
}
.command-palette__option.is-highlighted {
  color: var(--p-primary-600);
  background: color-mix(in srgb, var(--p-primary-500) 10%, var(--p-surface-0));
}
.command-palette__option-icon {
  flex: 0 0 auto;
  color: var(--p-surface-500);
}
.command-palette__option.is-highlighted .command-palette__option-icon {
  color: inherit;
}
.command-palette__option-text {
  display: flex;
  flex: 1 1 auto;
  gap: 0.4rem;
  align-items: baseline;
  min-width: 0;
}
.command-palette__option-parent {
  font-size: 0.857rem;
  color: var(--p-surface-500);
}
.command-palette__option-parent::after {
  content: '›';
  margin-left: 0.4rem;
}
.command-palette__option-title {
  font-weight: 500;
}
.command-palette__enter {
  flex: 0 0 auto;
  opacity: 0.6;
}
.command-palette__empty {
  margin: 0;
  padding: 1.25rem;
  font-size: 0.929rem;
  color: var(--p-surface-500);
  text-align: center;
}
</style>
